import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DashboardOutlined,
  InfoCircleOutlined,
  LogoutOutlined,
  MenuOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Avatar, Button, Divider, Dropdown, Layout, Space, Tag, Tooltip, Typography } from "antd";
import * as Images from "@/Assets/Index";
import { useAuth } from "@/Context/AuthContext";
import NavigationMenu from "../../App/Navigation";
import { UserContext } from "../../Context/UserContext";

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

export const CommonHeader = ({ children }) => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const isLoggedIn = localStorage.getItem("authorization");
  const { userProfileData } = useContext(UserContext);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isNavigationVisible, setIsNavigationVisible] = useState(false);
  const [useEffectCompleted, setUseEffectCompleted] = useState(false);

  const handleNavigationToggle = useCallback(() => {
    setIsNavigationVisible((prevState) => !prevState);
  }, []);

  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
  };

  const logo = {
    src: Images.AppLogo,
    width: 180,
    alt: "Upresume.ai",
    onClick: () => navigate("/app/resume-choice"),
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 767) {
        setIsNavigationVisible(false);
      }
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const toggleIsUserMenuOpen = useCallback(() => setIsUserMenuOpen((isUserMenuOpen) => !isUserMenuOpen), []);

  // Get user initials for avatar
  const getInitials = () => {
    const firstName = userProfileData?.firstName || "";
    const lastName = userProfileData?.lastName || "";
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || "U";
  };

  // Custom dropdown content
  const dropdownContent = (
    <div
      style={{
        background: "#fff",
        borderRadius: "12px",
        boxShadow: "0 6px 16px rgba(0,0,0,0.12)",
        padding: "8px 0",
        minWidth: "220px",
      }}
    >
      {/* Menu Items */}
      <div style={{ padding: "4px 8px" }}>
        <div
          onClick={() => {
            navigate("/app/dashboard");
            setIsUserMenuOpen(false);
          }}
          style={{
            display: "flex",
            alignItems: "center",
            padding: "12px 16px",
            cursor: "pointer",
            borderRadius: "8px",
            transition: "background 0.2s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#f5f5f5")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
        >
          <DashboardOutlined style={{ fontSize: "18px", color: "#595959", marginRight: "12px" }} />
          <Text style={{ fontSize: "15px" }}>Dashboard</Text>
        </div>

        <div
          onClick={handleLogout}
          style={{
            display: "flex",
            alignItems: "center",
            padding: "12px 16px",
            cursor: "pointer",
            borderRadius: "8px",
            transition: "background 0.2s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#f5f5f5")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
        >
          <LogoutOutlined style={{ fontSize: "18px", color: "#595959", marginRight: "12px" }} />
          <Text style={{ fontSize: "15px" }}>Logout</Text>
        </div>
      </div>

      {/* User Profile Section */}
      <div
        onClick={() => {
          navigate("/app/account");
          setIsUserMenuOpen(false);
        }}
        style={{
          display: "flex",
          alignItems: "center",
          padding: "16px",
          cursor: "pointer",
          borderTop: "1px solid #f0f0f0",
          marginTop: "8px",
          transition: "background 0.2s",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = "#fafafa")}
        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
      >
        <Avatar
          size={44}
          style={{
            backgroundColor: "#1890ff",
            fontSize: "16px",
            fontWeight: 600,
          }}
        >
          {getInitials()}
        </Avatar>
        <div style={{ marginLeft: "12px" }}>
          <Text strong style={{ display: "block", fontSize: "15px" }}>
            {userProfileData?.firstName} {userProfileData?.lastName}
          </Text>
          <Text style={{ fontSize: "13px", color: (userProfileData?.credits?.balance ?? 0) > 0 ? "#52c41a" : "#ff4d4f", fontWeight: 500 }}>
            ⚡ {userProfileData?.credits?.balance ?? 0} Credits
          </Text>
        </div>
      </div>
    </div>
  );

  const userMenuItems = [
    {
      key: "profile",
      label: userProfileData?.firstName || "User",
    },
  ];

  // Get plan info from user profile
  const userPlan = userProfileData?.plan || "free";
  const isPremiumPlan = userPlan === "premium";

  const userMenuMarkup = isLoggedIn && userProfileData && userProfileData.firstName && (
    <Space align="center" size="middle">
      {/* Free Plan Badge with Info Tooltip */}
      {!isPremiumPlan && (
        <Tooltip
          title={
            <div>
              <strong>Free Plan Features:</strong>
              <ul style={{ margin: "8px 0 0 0", paddingLeft: "16px" }}>
                <li>Create up to 1 resume</li>
                <li>Basic templates</li>
                <li>AI-powered enhancements</li>
              </ul>
              <div style={{ marginTop: "8px", fontSize: "12px", color: "#faad14" }}>
                Upgrade to Premium for unlimited resumes!
              </div>
            </div>
          }
          placement="bottomRight"
        >
          <Tag
            color="blue"
            style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "4px" }}
          >
            Free Plan <InfoCircleOutlined />
          </Tag>
        </Tooltip>
      )}
      <Dropdown
        dropdownRender={() => dropdownContent}
        trigger={["click"]}
        open={isUserMenuOpen}
        onOpenChange={toggleIsUserMenuOpen}
        placement="bottomRight"
      >
        <Avatar
          size={40}
          style={{
            backgroundColor: "#1890ff",
            cursor: "pointer",
            fontSize: "16px",
            fontWeight: 600,
          }}
        >
          {getInitials()}
        </Avatar>
      </Dropdown>
    </Space>
  );

  return (
    <Layout style={{ minHeight: "100vh", marginTop: "-70px" }}>
      <Header
        style={{
          padding: "0 24px",
          background: "#fff",
          borderBottom: "1px solid #f0f0f0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Space align="center">
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={handleNavigationToggle}
            style={{ display: window.innerWidth <= 767 ? "block" : "none" }}
          />
          <img
            src={logo.src}
            width={logo.width}
            alt={logo.alt}
            onClick={logo.onClick}
            style={{ cursor: "pointer" }}
          />
        </Space>
        {userMenuMarkup}
      </Header>
      <Layout>
        {isNavigationVisible && (
          <Sider
            width={250}
            style={{ background: "#fff" }}
            breakpoint="md"
            collapsedWidth="0"
            onBreakpoint={(broken) => {
              if (!broken) {
                setIsNavigationVisible(false);
              }
            }}
          >
            <NavigationMenu
              setUseEffectCompleted={setUseEffectCompleted}
              setIsNavigationVisible={setIsNavigationVisible}
            />
          </Sider>
        )}
        <Content style={{ padding: "24px", minHeight: "calc(100vh - 64px)" }}>{children}</Content>
      </Layout>
    </Layout>
  );
};
